import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", message: "" };

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useSEO({ title: job ? `${job.title} | Careers at Esprit Home Healthcare` : "Job Opening | Esprit Home Healthcare" });

  useEffect(() => {
    api.get(`/jobs/${slug}`).then((r) => setJob(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", { ...form, job_id: job.id, job_title: job.title });
      setDone(true);
      toast.success("Application submitted! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit application.");
    } finally {
      setLoading(false);
    }
  };

  if (notFound)
    return (
      <div className="container-page py-32 text-center">
        <h1 className="font-heading text-4xl text-primary">Position not found</h1>
        <Link to="/careers" className="inline-flex items-center gap-2 text-primary font-semibold mt-4"><ArrowLeft className="w-5 h-5" /> Back to Careers</Link>
      </div>
    );

  if (!job) return <div className="container-page py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div>
      <section className="bg-[hsl(var(--muted))] border-b border-border">
        <div className="container-page py-12 lg:py-16">
          <Link to="/careers" className="inline-flex items-center gap-2 text-primary font-semibold mb-6"><ArrowLeft className="w-5 h-5" /> All Openings</Link>
          <h1 className="font-heading text-4xl md:text-5xl text-primary">{job.title}</h1>
          <div className="flex flex-wrap gap-3 mt-4 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border px-4 py-1.5"><MapPin className="w-4 h-4" /> {job.location}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border px-4 py-1.5"><Briefcase className="w-4 h-4" /> {job.employment_type}</span>
            {job.department && <span className="rounded-full bg-secondary/15 text-secondary px-4 py-1.5 font-medium">{job.department}</span>}
          </div>
        </div>
      </section>

      <section className="container-page py-16 grid lg:grid-cols-5 gap-12 items-start">
        <Reveal className="lg:col-span-3">
          <h2 className="font-heading text-3xl text-primary">About the Role</h2>
          <p className="text-lg text-foreground/80 mt-3 leading-relaxed">{job.description}</p>
          {job.requirements?.length > 0 && (
            <>
              <h3 className="font-heading text-2xl text-primary mt-8">Requirements</h3>
              <ul className="space-y-3 mt-4">
                {job.requirements.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-foreground/90 text-lg">
                    <CheckCircle2 className="w-6 h-6 text-secondary shrink-0 mt-0.5" /> {r}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2 w-full">
          <div className="bg-[hsl(var(--muted))] rounded-[2rem] border border-border p-6 sm:p-8 lg:sticky lg:top-28">
            {done ? (
              <div data-testid="application-success" className="text-center py-8">
                <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-3" />
                <h3 className="font-heading text-2xl text-primary">Application Received!</h3>
                <p className="text-muted-foreground mt-2">Thank you for your interest. Our team will review and reach out.</p>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-2xl text-primary mb-4">Apply for this Position</h3>
                <form data-testid="application-form" onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="ap-name" className="text-base">Full Name *</Label>
                    <Input id="ap-name" data-testid="app-name-input" required value={form.name} onChange={set("name")} className="h-13 text-lg mt-1.5 bg-white py-3" />
                  </div>
                  <div>
                    <Label htmlFor="ap-email" className="text-base">Email *</Label>
                    <Input id="ap-email" type="email" data-testid="app-email-input" required value={form.email} onChange={set("email")} className="h-13 text-lg mt-1.5 bg-white py-3" />
                  </div>
                  <div>
                    <Label htmlFor="ap-phone" className="text-base">Phone *</Label>
                    <Input id="ap-phone" data-testid="app-phone-input" required value={form.phone} onChange={set("phone")} className="h-13 text-lg mt-1.5 bg-white py-3" />
                  </div>
                  <div>
                    <Label htmlFor="ap-msg" className="text-base">Cover Note (optional)</Label>
                    <Textarea id="ap-msg" data-testid="app-message-input" value={form.message} onChange={set("message")} className="text-lg mt-1.5 bg-white min-h-[100px]" placeholder="Tell us about your experience…" />
                  </div>
                  <button type="submit" data-testid="app-submit-btn" disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 text-lg font-bold transition-colors disabled:opacity-60">
                    {loading && <Loader2 className="w-5 h-5 animate-spin" />} Submit Application
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
